// src/pages/rapim/RapimPresentasi.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ChevronLeft, ChevronRight, X, Maximize2, Minimize2,
  FileText, Target, Users, Clock,
  CheckCircle, Loader2,
} from 'lucide-react';
import { Badge, ProgressBar } from '../../components/ui';
import { rapimService } from '../../services/rapim';
import { RapatPimpinan } from '../../types';
import toast from 'react-hot-toast';

interface Slide {
  id: number;
  type: 'cover' | 'laporan' | 'summary' | 'closing';
  title: string;
  data?: any;
}

const RapimPresentasi: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [slides, setSlides] = useState<Slide[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const res = await rapimService.getPresentationData(Number(id));
        // The API returns { success: true, data: { rapat: ..., summary: ... } }
        if (res.success && res.data) {
          generateSlides(res.data.rapat, res.data.summary);
        } else {
          // Attempt fallback for other structures
          const rapat = (res as any)?.data || res;
          generateSlides(rapat);
        }
      } catch (e) {
        console.error(e);
        toast.error('Gagal memuat data presentasi');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const generateSlides = (rapat: RapatPimpinan, summary?: any) => {
    const generatedSlides: Slide[] = [];

    // 1. Cover Slide
    generatedSlides.push({
      id: 1,
      type: 'cover',
      title: rapat.judul,
      data: {
        tanggal: new Date(rapat.tanggal).toLocaleDateString('id-ID', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        tempat: rapat.tempat || 'Online / Tidak ditentukan',
        peserta: rapat.peserta?.length || 0,
        agenda: (rapat.laporan?.length || rapat.laporan_masalah?.length || 0),
      },
    });

    // 2. Laporan Slides (Handle both 'laporan' and 'laporan_masalah' property names)
    const laporanList = rapat.laporan || rapat.laporan_masalah || [];
    laporanList.forEach((lap, index) => {
      const laporanData = lap as any;
      generatedSlides.push({
        id: generatedSlides.length + 1,
        type: 'laporan',
        title: lap.judul.length > 50 ? lap.judul.substring(0, 50) + '...' : lap.judul,
        data: {
          kode: lap.kode,
          judul: lap.judul,
          unit: lap.unit?.name || '-',
          pelapor: laporanData.user?.name
            || laporanData.pelapor?.name
            || laporanData.pelapor
            || laporanData.user_name
            || '-',
          risiko: lap.tingkat_risiko,
          deskripsi: lap.deskripsi,
          dampak: lap.dampak || '-',
          akar_masalah: lap.akar_masalah || '-',
          kategori: laporanData.kategori_masalah?.nama || laporanData.kategori?.nama || '-',
        },
      });
    });

    // 3. Summary Slide (Ensure detailed stats are available)
    const reports = laporanList;
    if (reports.length > 0) {
      const units = Array.from(new Set(reports.map(l => l.unit?.name).filter(Boolean)));
      const derivedStats = {
        total: reports.length,
        kritis: reports.filter(l => l.tingkat_risiko === 'kritis' || (l as any).tingkat_risiko === 'Kritis').length,
        tinggi: reports.filter(l => l.tingkat_risiko === 'tinggi' || (l as any).tingkat_risiko === 'Tinggi').length,
        sedang: reports.filter(l => l.tingkat_risiko === 'sedang' || (l as any).tingkat_risiko === 'Sedang').length,
        unitTerdampak: units.slice(0, 5) as string[],
      };

      generatedSlides.push({
        id: generatedSlides.length + 1,
        type: 'summary',
        title: 'Ringkasan & Statistik',
        data: derivedStats
      });
    }

    // 4. Closing Slide
    generatedSlides.push({
      id: generatedSlides.length + 1,
      type: 'closing',
      title: 'Penutup',
      data: {
        nextSteps: [
          'Monitoring keputusan yang telah disepakati',
          'Pastikan notulen rapat segera didistribusikan',
          'Update progress setiap 2 minggu',
        ],
      },
    });

    setSlides(generatedSlides);
  };

  const totalSlides = slides.length;
  const slide = slides[currentSlide];

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.min(prev + 1, totalSlides - 1));
  }, [totalSlides]);

  const prevSlide = useCallback(() => {
    setCurrentSlide((prev) => Math.max(prev - 1, 0));
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        nextSlide();
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        prevSlide();
      }
      if (e.key === 'Escape') {
        navigate(`/rapim/${id}`);
      }
      if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [nextSlide, prevSlide, navigate, id]);

  const exitPresentation = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    }
    navigate(`/rapim/${id}`);
  };

  const renderSlide = () => {
    if (!slide) return null;

    switch (slide.type) {
      case 'cover':
        return (
          <div className="flex flex-col items-center justify-center h-full text-center px-8">
            <div className="w-20 h-20 bg-white/10 rounded-2xl flex items-center justify-center mb-8">
              <span className="text-4xl font-black text-white">SE</span>
            </div>
            <h1 className="text-5xl font-black text-white tracking-tight mb-4">
              {slide.title}
            </h1>
            <p className="text-xl text-white/70 mb-8">
              Universitas Buana Perjuangan Karawang
            </p>
            <div className="flex items-center gap-8 text-white/60">
              <span className="flex items-center gap-2"><Clock className="w-5 h-5" /> {slide.data.tanggal}</span>
              <span className="flex items-center gap-2"><Target className="w-5 h-5" /> {slide.data.tempat}</span>
              <span className="flex items-center gap-2"><Users className="w-5 h-5" /> {slide.data.peserta} peserta</span>
              <span className="flex items-center gap-2"><FileText className="w-5 h-5" /> {slide.data.agenda} agenda</span>
            </div>
          </div>
        );

      case 'laporan':
        return (
          <div className="flex flex-col h-full px-12 py-8">
            {/* Title Bar */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-sm font-mono text-teal-light bg-white/10 px-3 py-1 rounded-full">
                    {slide.data.kode}
                  </span>
                  <Badge variant="risk" risk={slide.data.risiko} size="md" />
                </div>
                <h2 className="text-3xl font-black text-white">{slide.data.judul}</h2>
              </div>
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-2 gap-6 flex-1">
              {/* Left */}
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-2">Deskripsi</p>
                  <p className="text-sm text-white/90 leading-relaxed">{slide.data.deskripsi}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-2">Dampak</p>
                  <p className="text-sm text-white/90 leading-relaxed">{slide.data.dampak}</p>
                </div>
              </div>

              {/* Right */}
              <div className="space-y-5">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-2">Akar Masalah</p>
                  <p className="text-sm text-white/90 leading-relaxed">{slide.data.akar_masalah}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-xs text-white/40 mb-1">Unit</p>
                    <p className="text-sm font-semibold text-white">{slide.data.unit}</p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl">
                    <p className="text-xs text-white/40 mb-1">Pelapor</p>
                    <p className="text-sm font-semibold text-white">{slide.data.pelapor}</p>
                  </div>
                  <div className="col-span-2 p-4 bg-white/5 rounded-xl">
                    <p className="text-xs text-white/40 mb-1">Kategori Laporan</p>
                    <p className="text-sm font-semibold text-white">{slide.data.kategori}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'summary':
        return (
          <div className="flex flex-col items-center justify-center h-full px-12">
            <h2 className="text-4xl font-black text-white mb-12">{slide.title}</h2>
            <div className="grid grid-cols-4 gap-6 w-full max-w-3xl mb-12">
              <div className="bg-white/10 rounded-2xl p-6 text-center">
                <p className="text-4xl font-black text-white">{slide.data.total}</p>
                <p className="text-sm text-white/60 mt-1">Total Laporan</p>
              </div>
              <div className="bg-red-500/20 rounded-2xl p-6 text-center">
                <p className="text-4xl font-black text-red-400">{slide.data.kritis}</p>
                <p className="text-sm text-white/60 mt-1">Kritis</p>
              </div>
              <div className="bg-orange-500/20 rounded-2xl p-6 text-center">
                <p className="text-4xl font-black text-orange-400">{slide.data.tinggi}</p>
                <p className="text-sm text-white/60 mt-1">Tinggi</p>
              </div>
              <div className="bg-yellow-500/20 rounded-2xl p-6 text-center">
                <p className="text-4xl font-black text-yellow-400">{slide.data.sedang}</p>
                <p className="text-sm text-white/60 mt-1">Sedang</p>
              </div>
            </div>
            <div className="text-center">
              <p className="text-sm text-white/40 uppercase tracking-widest mb-2">Unit Terdampak</p>
              <div className="flex items-center gap-3 justify-center">
                {slide.data.unitTerdampak.map((u: string, i: number) => (
                  <span key={i} className="px-4 py-2 bg-white/10 rounded-full text-sm text-white font-medium">
                    {u}
                  </span>
                ))}
              </div>
            </div>
          </div>
        );

      case 'closing':
        return (
          <div className="flex flex-col items-center justify-center h-full px-12 text-center">
            <CheckCircle className="w-16 h-16 text-teal-light mb-6" />
            <h2 className="text-4xl font-black text-white mb-8">Tindak Lanjut</h2>
            <div className="space-y-4 w-full max-w-xl">
              {slide.data.nextSteps.map((step: string, i: number) => (
                <div key={i} className="flex items-center gap-4 bg-white/10 rounded-xl p-4">
                  <div className="w-8 h-8 bg-teal-light rounded-full flex items-center justify-center text-sm font-bold text-teal-dark shrink-0">
                    {i + 1}
                  </div>
                  <p className="text-sm text-white text-left">{step}</p>
                </div>
              ))}
            </div>
            <p className="text-white/30 mt-12 text-sm">
              Terima kasih • SIM-EVAL UBP Karawang
            </p>
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-neutral-900 flex items-center justify-center">
        <Loader2 className="w-12 h-12 animate-spin text-teal-light" />
      </div>
    );
  }

  if (slides.length === 0) {
    return (
      <div className="fixed inset-0 bg-neutral-900 flex flex-col items-center justify-center text-white">
        <p>Tidak ada data presentasi.</p>
        <button
          onClick={() => navigate(`/rapim/${id}`)}
          className="mt-4 text-teal-light hover:underline"
        >
          Kembali ke Detail Rapat
        </button>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-gradient-to-br from-teal-dark via-[#0a1628] to-[#0f2027] z-[100]">
      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 h-12 flex items-center justify-between px-4 bg-black/20 z-10 transition-opacity hover:opacity-100 opacity-0 md:opacity-100">
        <div className="flex items-center gap-3">
          <span className="text-xs text-white/60 font-mono">
            {currentSlide + 1} / {totalSlides}
          </span>
          <span className="text-xs text-white/40">|</span>
          <span className="text-xs text-white/60">{slide?.title}</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFullscreen}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
          <button
            onClick={exitPresentation}
            className="p-1.5 rounded-lg hover:bg-white/10 text-white/60 hover:text-white transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Slide Content */}
      <div className="h-full pt-12 pb-16">
        {renderSlide()}
      </div>

      {/* Bottom Navigation */}
      <div className="absolute bottom-0 left-0 right-0 h-16 flex items-center justify-between px-6 bg-black/20 transition-opacity hover:opacity-100 opacity-0 md:opacity-100">
        <button
          onClick={prevSlide}
          disabled={currentSlide === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          <ChevronLeft className="w-5 h-5" /> Sebelumnya
        </button>

        {/* Progress Dots */}
        <div className="flex items-center gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentSlide(i)}
              className={`w-2.5 h-2.5 rounded-full transition-all ${i === currentSlide ? 'bg-teal-light w-8' : 'bg-white/30 hover:bg-white/50'
                }`}
            />
          ))}
        </div>

        <button
          onClick={nextSlide}
          disabled={currentSlide === totalSlides - 1}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white/70 hover:text-white hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
        >
          Selanjutnya <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Keyboard Hint */}
      <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-[10px] text-white/20 select-none">
        ← → Navigasi • F Fullscreen • ESC Keluar
      </div>
    </div>
  );
};

export default RapimPresentasi;
